// Types and wording for the Live Fleet page. No figure is computed here: the
// state of every car, and the minutes since it last reported, come from
// v_vehicle_live_status (db/migrations/0366). This module only says them.

export type LiveState = 'moving' | 'idling' | 'parked' | 'no_signal' | 'no_tracker'

export type LiveFleetRow = {
  vehicle_id: string
  plate: string | null
  vehicle: string | null
  operational: boolean | null
  operational_state: string | null
  manager_person_id: string | null
  manager_name: string | null
  driver_names: string | null
  device_role: string | null
  event_ts: string | null
  minutes_since_report: number | null
  latitude: number | null
  longitude: number | null
  position_description: string | null
  bearing: number | null
  speed_kmh: number | null
  ignition: boolean | null
  idling: boolean | null
  odometer_km: number | null
  live_state: LiveState
  live_rank: number
  feed_read_at: string | null
  camera_status: 'online' | 'standby' | 'offline' | 'none'
  camera_channels: { camera: number; online: boolean }[]
  camera_checked_at: string | null
  can_watch: boolean
}

export type LiveFleetResult = {
  scope: 'mine' | 'all'
  personLinked: boolean
  rows: LiveFleetRow[]
}

export const LIVE_STATES: LiveState[] = ['moving', 'idling', 'parked', 'no_signal', 'no_tracker']

export const LIVE_STATE_LABEL: Record<LiveState, string> = {
  moving: 'Moving',
  idling: 'Idling',
  parked: 'Parked',
  no_signal: 'No signal',
  no_tracker: 'No tracker',
}

export function liveTone(state: LiveState): 'green' | 'amber' | 'neutral' | 'red' {
  switch (state) {
    case 'moving':
      return 'green'
    case 'idling':
      return 'amber'
    case 'parked':
      return 'neutral'
    default:
      return 'red'
  }
}

/** "just now", "12 min ago", "3 h ago", "4 days ago". Null means never. */
export function agoPhrase(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return 'never reported'
  if (minutes < 2) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h ago`
  const days = Math.round(minutes / 1440)
  return `${days} days ago`
}

/** One line a manager can read at a glance. */
export function statusLine(r: LiveFleetRow): string {
  switch (r.live_state) {
    case 'moving':
      return `${r.speed_kmh ?? 0} km/h`
    case 'idling':
      return 'Engine on, standing'
    case 'parked':
      return `Parked, last report ${agoPhrase(r.minutes_since_report)}`
    case 'no_signal':
      return r.minutes_since_report === null
        ? 'Tracker linked but has never reported'
        : `Last heard ${agoPhrase(r.minutes_since_report)}`
    case 'no_tracker':
      return 'No Cartrack unit linked to this car'
  }
}

export function hasPosition(r: LiveFleetRow): r is LiveFleetRow & { latitude: number; longitude: number } {
  return typeof r.latitude === 'number' && typeof r.longitude === 'number'
}

/** Johannesburg wall-clock time, HH:MM, for "feed updated at". */
export function sastTime(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}
